import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { OrdenServicioRepository } from 'src/app/domain/service/orden-servicio/orden-servicio-repository';
import {
  OrdenServicio,
  OrdenServicioFiltro,
  OrdenServicioPageResponse,
  InformacionTareaFiltro,
  InformacionTareaPageResponse,
  OrdenServicioConfiguracion,
  InformacionTareasConfiguracion,
  OrdenServicioDetalleProgramar,
  ProgramacionServicio,
  TareaBeneficiario,
  TareaBeneficiarioPage,
  AsesorSugerido
} from 'src/app/domain/model/orden-servicio/orden-servicio';
import { getDniAutenticado } from 'src/app/common/auth-utils';
import { validarPageSize } from 'src/app/common/validation-utils';

/**
 * Adaptador HTTP: HttpOrdenServicioRepository
 *
 * Implementa el puerto OrdenServicioRepository para comunicación HTTP con backend.
 * Realiza llamadas REST al backend Spring Boot (OrdenServicioController).
 *
 * Arquitectura Hexagonal:
 * - Este adaptador está en infraestructura
 * - Implementa el puerto OrdenServicioRepository del dominio
 *
 * Otros dominios tienen sus propios adaptadores:
 * - HttpAseguradoraRepository - /api/servicios/aseguradoras
 * - HttpCategoriaClienteRepository - /api/servicios/categorias-cliente
 * - HttpClienteMacrosegRepository - /api/servicios/macrosegmentaciones
 * - HttpClienteMicrosegRepository - /api/servicios/microsegmentaciones
 * - HttpOficinaRepository - /api/servicios/oficinas
 * - HttpUnidadAdministrativaRepository - /api/servicios/unidades-administrativas
 *
 * Backend: 580-girop_servicios-ms (puerto 8080)
 *
 * @author Sura - Migración GWT a Spring Boot
 */
@Injectable({ providedIn: 'root' })
export class HttpOrdenServicioRepository implements OrdenServicioRepository {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/ordenes-servicio`;
  private readonly informacionTareasUrl = `${environment.backend.baseUrl}/servicios/ordenes-servicio/informacion-tareas`;

  /**
   * Obtiene el DNI del usuario actual desde SSO.
   */
  private getDni(): string {
    return getDniAutenticado();
  }

  /**
   * Construye los headers HTTP con el DNI del usuario.
   */
  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'X-User-DNI': this.getDni()
    });
  }

  // === Consulta de Órdenes ===

  async consultarInformacionTareas(
    filtro: InformacionTareaFiltro
  ): Promise<InformacionTareaPageResponse> {
    let params = new HttpParams();

    if (filtro.orden) {
      params = params.set('orden', filtro.orden);
    }
    if (filtro.fechaEntregaDesde) {
      params = params.set('fechaEntregaDesde', filtro.fechaEntregaDesde);
    }
    if (filtro.fechaEntregaHasta) {
      params = params.set('fechaEntregaHasta', filtro.fechaEntregaHasta);
    }
    if (filtro.nmconsecutivoBeneficiario) {
      params = params.set(
        'nmconsecutivoBeneficiario',
        filtro.nmconsecutivoBeneficiario.toString()
      );
    }
    if (filtro.nmconsecutivoAsesor) {
      params = params.set(
        'nmconsecutivoAsesor',
        filtro.nmconsecutivoAsesor.toString()
      );
    }
    if (
      filtro.nmconsecutivoOficina !== undefined &&
      filtro.nmconsecutivoOficina !== null
    ) {
      params = params.set(
        'nmconsecutivoOficina',
        filtro.nmconsecutivoOficina.toString()
      );
    }

    params = params.set('page', (filtro.page ?? 0).toString());
    params = params.set('size', (filtro.size ?? 10).toString());

    return firstValueFrom(
      this.http.get<InformacionTareaPageResponse>(this.informacionTareasUrl, {
        params,
        headers: this.getHeaders()
      })
    );
  }

  async obtenerConfiguracionInformacionTareas(): Promise<InformacionTareasConfiguracion> {
    return firstValueFrom(
      this.http.get<InformacionTareasConfiguracion>(
        `${this.baseUrl}/informacion-tareas/configuracion`,
        { headers: this.getHeaders() }
      )
    );
  }

  async consultarOrdenes(
    filtro: OrdenServicioFiltro
  ): Promise<OrdenServicioPageResponse> {
    let params = new HttpParams();

    // Cliente
    if (filtro.cdcliente) {
      params = params.set('cdcliente', filtro.cdcliente.toString());
    }
    if (filtro.tipoIdCliente) {
      params = params.set('tipoIdCliente', filtro.tipoIdCliente);
    }
    if (filtro.numeroIdCliente) {
      params = params.set('numeroIdCliente', filtro.numeroIdCliente);
    }

    // Beneficiario
    if (filtro.cdbenef) {
      params = params.set('cdbenef', filtro.cdbenef.toString());
    }
    if (filtro.tipoIdBenef) {
      params = params.set('tipoIdBenef', filtro.tipoIdBenef);
    }
    if (filtro.numeroIdBenef) {
      params = params.set('numeroIdBenef', filtro.numeroIdBenef);
    }

    // Fechas (obligatorias)
    if (filtro.feaprobacionIni) {
      params = params.set('feaprobacionIni', filtro.feaprobacionIni);
    }
    if (filtro.feaprobacionFin) {
      params = params.set('feaprobacionFin', filtro.feaprobacionFin);
    }

    // Consecutivo manual
    if (filtro.consecutivoManualEncab) {
      params = params.set(
        'consecutivoManualEncab',
        filtro.consecutivoManualEncab
      );
    }

    // Segmentación
    if (filtro.cdtipoMacroseg) {
      params = params.set('cdtipoMacroseg', filtro.cdtipoMacroseg.toString());
    }
    if (filtro.cdtipoMicroseg) {
      params = params.set('cdtipoMicroseg', filtro.cdtipoMicroseg.toString());
    }
    if (filtro.cdtipoAtencion) {
      params = params.set('cdtipoAtencion', filtro.cdtipoAtencion.toString());
    }

    // Filtros adicionales
    if (filtro.snprioridad) {
      params = params.set('snprioridad', filtro.snprioridad);
    }
    if (filtro.snprogramado) {
      params = params.set('snprogramado', filtro.snprogramado);
    }
    if (filtro.sntareaProgramable) {
      params = params.set('sntareaProgramable', filtro.sntareaProgramable);
    }
    if (filtro.cdunidadAdmin) {
      params = params.set('cdunidadAdmin', filtro.cdunidadAdmin);
    }
    if (filtro.nmconsUnidadAdmin) {
      params = params.set(
        'nmconsUnidadAdmin',
        filtro.nmconsUnidadAdmin.toString()
      );
    }

    // Fechas de entrega inicial
    if (filtro.feentregaIniDesde) {
      params = params.set('feentregaIniDesde', filtro.feentregaIniDesde);
    }
    if (filtro.feentregaIniHasta) {
      params = params.set('feentregaIniHasta', filtro.feentregaIniHasta);
    }

    // Oficina
    if (filtro.nmoficina) {
      params = params.set('nmoficina', filtro.nmoficina.toString());
    }

    // Tarea/Homologación
    if (filtro.nmconsecutivoTarea) {
      params = params.set(
        'nmconsecutivoTarea',
        filtro.nmconsecutivoTarea.toString()
      );
    }
    if (filtro.nmhomologacionTarea) {
      params = params.set(
        'nmhomologacionTarea',
        filtro.nmhomologacionTarea.toString()
      );
    }

    // Centro de trabajo
    if (filtro.nmcentroTrabajo) {
      params = params.set('nmcentroTrabajo', filtro.nmcentroTrabajo.toString());
    }

    // Profesional
    if (filtro.dniProfesional) {
      params = params.set('dniProfesional', filtro.dniProfesional);
    }

    // Asesor/Consultor (solo tipo y número de identificación)
    if (filtro.tipoIdAsesor) {
      params = params.set('tipoIdAsesor', filtro.tipoIdAsesor);
    }
    if (filtro.numeroIdAsesor) {
      params = params.set('numeroIdAsesor', filtro.numeroIdAsesor);
    }

    // Rango de llamadas
    if (filtro.rangoLlamDesde !== undefined && filtro.rangoLlamDesde !== null) {
      params = params.set('rangoLlamDesde', filtro.rangoLlamDesde.toString());
    }
    if (filtro.rangoLlamHasta !== undefined && filtro.rangoLlamHasta !== null) {
      params = params.set('rangoLlamHasta', filtro.rangoLlamHasta.toString());
    }

    // Llamadas efectivas
    if (filtro.snllamadaEfect) {
      params = params.set('snllamadaEfect', filtro.snllamadaEfect);
    }

    // Paginación
    const page = filtro.page ?? 0;
    const size = filtro.size ?? 10;
    validarPageSize(page, size);
    params = params.set('page', page.toString());
    params = params.set('size', size.toString());

    return firstValueFrom(
      this.http.get<OrdenServicioPageResponse>(`${this.baseUrl}/search`, {
        params,
        headers: this.getHeaders()
      })
    );
  }

  async obtenerOrdenPorConsecutivo(
    nmconsecutivo: number
  ): Promise<OrdenServicio> {
    return firstValueFrom(
      this.http.get<OrdenServicio>(`${this.baseUrl}/${nmconsecutivo}`)
    );
  }

  // === Bloqueo/Desbloqueo ===

  async bloquearOrden(nmconsecutivo: number): Promise<void> {
    return firstValueFrom(
      this.http.post<void>(`${this.baseUrl}/${nmconsecutivo}/bloquear`, {})
    );
  }

  async desbloquearOrden(nmconsecutivo: number): Promise<void> {
    return firstValueFrom(
      this.http.post<void>(`${this.baseUrl}/${nmconsecutivo}/desbloquear`, {})
    );
  }

  // === Programar Servicio ===

  async obtenerDetalle(
    nmconsecutivo: number
  ): Promise<OrdenServicioDetalleProgramar> {
    return firstValueFrom(
      this.http.get<OrdenServicioDetalleProgramar>(
        `${this.baseUrl}/${nmconsecutivo}/detalle`,
        { headers: this.getHeaders() }
      )
    );
  }

  async consultarProgramaciones(
    nmconsecutivo: number
  ): Promise<ProgramacionServicio[]> {
    const url = `${this.baseUrl}/${nmconsecutivo}/programaciones`;
    console.log('[HttpOrdenServicio] GET programaciones URL:', url);
    return firstValueFrom(
      this.http.get<ProgramacionServicio[]>(url, { headers: this.getHeaders() })
    );
  }

  async consultarTareasBeneficiario(
    nmconsecutivoBeneficiario: number,
    nmconsecutivoOrdenActual: number,
    page: number = 0,
    size: number = 10
  ): Promise<TareaBeneficiarioPage> {
    validarPageSize(page, size);

    const params = new HttpParams()
      .set('nmconsecutivoBeneficiario', nmconsecutivoBeneficiario.toString())
      .set('page', page.toString())
      .set('size', size.toString());

    return firstValueFrom(
      this.http.get<TareaBeneficiarioPage>(
        `${this.baseUrl}/${nmconsecutivoOrdenActual}/tareas-beneficiario`,
        { params, headers: this.getHeaders() }
      )
    );
  }

  async obtenerConfiguracionActas(): Promise<OrdenServicioConfiguracion> {
    return firstValueFrom(
      this.http.get<OrdenServicioConfiguracion>(
        `${this.baseUrl}/configuracion`,
        { headers: this.getHeaders() }
      )
    );
  }

  // === Cumplimiento ===

  async actualizarContactoCumplimiento(
    nmconsecutivoCumplimiento: number,
    nombreContacto: string,
    emailContacto: string
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.put<boolean>(
        `${this.baseUrl}/cumplimientos/${nmconsecutivoCumplimiento}/contacto`,
        { nombreContacto, emailContacto },
        { headers: this.getHeaders() }
      )
    );
  }

  async modificarCumplimientoInformacionTareas(
    request: Record<string, unknown>
  ): Promise<Record<string, unknown>> {
    return firstValueFrom(
      this.http.post<Record<string, unknown>>(
        `${this.baseUrl}/cumplimientos/informacion-tareas/modificar`,
        request,
        { headers: this.getHeaders() }
      )
    );
  }

  // === Asociar Acta ===

  /**
   * Asocia un acta a un cumplimiento mediante el servicio externo.
   *
   * Legacy: ProgramarServiciosActivity.asociarActas()
   *
   * @param idCumplimiento ID del cumplimiento (CDCONSECUTIVO_CUMPLIMIENTO de TGFC_CUMPLIM_ACTA)
   * @param consecutivoActa Consecutivo del acta (CDCONSECUTIVO_ACTA de TGFC_CUMPLIM_ACTA)
   * @returns Objeto con exito y mensaje
   */
  async asociarActa(
    idCumplimiento: string,
    consecutivoActa: string
  ): Promise<{ exito: boolean; mensaje: string }> {
    console.log('[HttpOrdenServicio] Asociando acta:', {
      idCumplimiento,
      consecutivoActa
    });
    return firstValueFrom(
      this.http.post<{ exito: boolean; mensaje: string }>(
        `${this.baseUrl}/programaciones/0/asociar-acta`,
        { idCumplimiento, consecutivoActa },
        { headers: this.getHeaders() }
      )
    );
  }

  // === Asesores Sugeridos ===

  /**
   * Consulta los asesores sugeridos para programar una cita.
   *
   * Legacy: ScheduleAdvisorActivity.searchSuggestedAdvisors()
   * SP: PCK_PSC_PROGRAMAR_ORDENES.SP_GET_POSIBLES_ASESORES
   *
   * @param nmconsecutivoTarea Consecutivo del detalle de orden
   * @param incluirSugerido Incluir asesor pre-asignado (S/N, default S)
   * @param incluirAtendieron Incluir asesores que ya atendieron (S/N, default S)
   * @param incluirOtros Incluir otros asesores disponibles (S/N, default N)
   */
  async consultarAsesoresSugeridos(
    nmconsecutivoTarea: number,
    incluirSugerido = 'S',
    incluirAtendieron = 'S',
    incluirOtros = 'N'
  ): Promise<AsesorSugerido[]> {
    console.log('[HttpOrdenServicio] Consultando asesores sugeridos:', {
      nmconsecutivoTarea,
      incluirSugerido,
      incluirAtendieron,
      incluirOtros
    });

    let params = new HttpParams()
      .set('incluirSugerido', incluirSugerido)
      .set('incluirAtendieron', incluirAtendieron)
      .set('incluirOtros', incluirOtros);

    return firstValueFrom(
      this.http.get<AsesorSugerido[]>(
        `${this.baseUrl}/${nmconsecutivoTarea}/asesores-sugeridos`,
        { headers: this.getHeaders(), params }
      )
    );
  }
}
